import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Capacitor } from "@capacitor/core";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  ArrowLeft,
  Activity,
  Footprints,
  Route,
  Flame,
  Heart,
  Moon,
  Watch,
  Star,
  Loader2,
  RefreshCw,
} from "lucide-react";
import { HealthMetricChart } from "@/components/HealthMetricChart";
import { SleepTrackingCard } from "@/components/SleepTrackingCard";
import { useSleepTracking } from "@/hooks/useSleepTracking";
import { getHealthConnectedState, getLastHealthSyncTimestamp, presentHealthScreenNative, syncHealthToSupabase } from "@/lib/appleHealth";
import { useLastHealthSyncTime } from "@/hooks/useLastHealthSyncTime";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { useUnitFormatters } from "@/hooks/useUnits";
import { distanceUnitShort, kmToDisplayDistance } from "@/lib/units";
import { Analytics } from "@/lib/analytics";

const FAVORITE_GRAPHS_KEY = (uid: string) => `straiv_favorite_health_graphs_${uid}`;
/** Favorite list entry for the sleep card (not in ALL_CHART_METRICS) */
const SLEEP_FAVORITE_KEY = "sleep";

function loadFavoriteGraphs(userId: string | undefined): string[] {
  if (!userId || typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(FAVORITE_GRAPHS_KEY(userId));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveFavoriteGraphs(userId: string, metrics: string[]) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(FAVORITE_GRAPHS_KEY(userId), JSON.stringify(metrics));
  } catch {}
}

export const HealthData = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { units } = useUnitFormatters();
  const ALL_CHART_METRICS = useMemo(() => ([
    { metric: 'steps' as const, label: t('healthData.metrics.steps.label'), icon: Footprints, unit: 'steps', info: t('healthData.metrics.steps.info') },
    { metric: 'distance_km' as const, label: t('healthData.metrics.distance.label'), icon: Route, unit: distanceUnitShort(units), info: t('healthData.metrics.distance.info') },
    { metric: 'active_energy_kcal' as const, label: t('healthData.metrics.activeCalories.label'), icon: Flame, unit: 'kcal', info: t('healthData.metrics.activeCalories.info') },
    { metric: 'exercise_minutes' as const, label: t('healthData.metrics.exerciseMinutes.label'), icon: Activity, unit: 'min', info: t('healthData.metrics.exerciseMinutes.info') },
    { metric: 'stand_hours' as const, label: t('healthData.metrics.standHours.label'), icon: Activity, unit: 'hours', info: t('healthData.metrics.standHours.info') },
    { metric: 'flights_climbed' as const, label: t('healthData.metrics.flightsClimbed.label'), icon: Activity, unit: 'flights', info: t('healthData.metrics.flightsClimbed.info') },
    { metric: 'resting_heart_rate_bpm' as const, label: t('healthData.metrics.restingHeartRate.label'), icon: Heart, unit: 'bpm', info: t('healthData.metrics.restingHeartRate.info') },
    { metric: 'vo2_max_ml_per_kg_min' as const, label: t('healthData.metrics.vo2Max.label'), icon: Activity, unit: 'ml/kg·min', info: t('healthData.metrics.vo2Max.info') },
    { metric: 'stress_score' as const, label: t('healthData.metrics.stressScore.label'), icon: Heart, unit: '', info: t('healthData.metrics.stressScore.info') },
    { metric: 'strain' as const, label: t('healthData.metrics.strain.label'), icon: Activity, unit: '', info: t('healthData.metrics.strain.info') },
  ]), [t, units]);
  const [connected, setConnected] = useState<boolean | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [favoriteGraphs, setFavoriteGraphs] = useState<string[]>(() => loadFavoriteGraphs(user?.id));

  useEffect(() => {
    if (user?.id) setFavoriteGraphs(loadFavoriteGraphs(user.id));
  }, [user?.id]);

  const toggleFavorite = (metric: string) => {
    if (!user?.id) return;
    setFavoriteGraphs((prev) => {
      const next = prev.includes(metric) ? prev.filter((m) => m !== metric) : [...prev, metric];
      saveFavoriteGraphs(user.id, next);
      return next;
    });
  };
  const {
    sleepLogs,
    healthSleepData,
    logSleep,
    getWeeklyAverage: getSleepWeeklyAverage,
    getSleepTrend,
    refetch: refetchSleep,
  } = useSleepTracking();

  const isIOS = Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";
  const { formatted: lastSyncFormatted } = useLastHealthSyncTime(user?.id ?? null);

  useEffect(() => {
    if (user?.id) getHealthConnectedState(user.id).then(setConnected);
  }, [user?.id]);

  useEffect(() => {
    if (connected) refetchSleep();
  }, [connected, refetchSleep]);

  // Sync when Health tab mounts — skip if App.tsx already synced recently.
  useEffect(() => {
    if (connected && user?.id && isIOS) {
      const last = getLastHealthSyncTimestamp(user.id) ?? 0;
      if (last > 0 && Date.now() - last < 2 * 60 * 1000) return;
      syncHealthToSupabase(user.id, {
        forceSync: true,
        skipFeedback: true,
        lookbackDays: 14,
        skipSamples: true,
      }).catch(() => {});
    }
  }, [connected, user?.id, isIOS]);

  const handleConnectHealth = async () => {
    if (!user?.id) return;
    setIsConnecting(true);
    Analytics.healthConnectTapped();
    try {
      await presentHealthScreenNative({ syncOnDismiss: true, userId: user.id });
      const nowConnected = await getHealthConnectedState(user.id);
      setConnected(nowConnected);
      if (nowConnected) Analytics.healthConnected();
      else Analytics.healthAuthDenied();
    } finally {
      setIsConnecting(false);
    }
  };

  const handleUpdateData = async () => {
    if (!user?.id || !connected) return;
    setIsUpdating(true);
    Analytics.healthSyncStarted();
    try {
      const result = await syncHealthToSupabase(user.id, {
        forceSync: true,
        skipFeedback: true,
        lookbackDays: 30,
        skipSamples: true,
      });
      if (result.synced) {
        Analytics.healthSyncCompleted(true);
        refetchSleep();
        window.dispatchEvent(new CustomEvent("health-sync-complete"));
        toast.success(t('healthData.toast.updateSuccess'), {
          duration: 4000,
          id: "health-update-success",
          style: { zIndex: 2147483647 },
        });
      } else {
        Analytics.healthSyncCompleted(false, { error: result.error });
        toast.error(result.error ?? t('healthData.toast.updateFailed'), {
          duration: 5000,
          id: "health-update-error",
          style: { zIndex: 2147483647 },
        });
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('healthData.toast.updateFailed');
      Analytics.healthSyncCompleted(false, { error: msg });
      toast.error(msg, {
        duration: 5000,
        id: "health-update-error",
        style: { zIndex: 2147483647 },
      });
    } finally {
      setIsUpdating(false);
    }
  };

  if (!isIOS) {
    return (
      <div className="p-4 pb-24">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate("/food-log")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-normal">{t('healthData.title')}</h1>
        </div>
        <Card className="bg-gradient-card border-0">
          <CardContent className="pt-6">
            <p className="text-muted-foreground text-center">
              {t('healthData.iosOnlyMessage')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 flex-shrink-0 min-h-fit">
      <div className="flex items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/food-log")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-normal">{t('healthData.title')}</h1>
        </div>
        <div className="flex flex-col items-end gap-1">
          {connected && (
            <>
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 shrink-0"
                onClick={handleUpdateData}
                disabled={isUpdating}
              >
                {isUpdating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                {t('healthData.updateData')}
              </Button>
              {lastSyncFormatted && (
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {t('healthData.lastUpdated', { time: lastSyncFormatted })}
                </span>
              )}
            </>
          )}
        </div>
      </div>

      {connected === null && (
        <div className="py-8 text-center text-muted-foreground">{t('healthData.checkingConnection')}</div>
      )}

      {connected === false && !isConnecting && (
        <Card className="mb-6 border-primary/30 bg-primary/5">
          <CardContent className="pt-6 pb-6">
            <div className="flex flex-col items-center text-center gap-4">
              <div className="rounded-full bg-primary/20 p-4">
                <Watch className="h-10 w-10 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">{t('healthData.connect.title')}</h3>
                <p className="text-sm text-muted-foreground max-w-sm">
                  {t('healthData.connect.description')}
                </p>
              </div>
              <Button
                onClick={handleConnectHealth}
                disabled={isConnecting}
                className="gap-2 bg-gradient-primary text-black shadow-glow hover:shadow-lg"
                size="lg"
              >
                <Activity className="h-5 w-5" />
                {t('healthData.connect.button')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {connected === false && isConnecting && (
        <Card className="mb-6 border-primary/30 bg-primary/5">
          <CardContent className="pt-6 pb-6">
            <div className="flex flex-col items-center text-center gap-4 py-4">
              <Loader2 className="h-10 w-10 text-primary animate-spin" />
              <div>
                <h3 className="font-semibold text-lg mb-1">{t('healthData.syncing.title')}</h3>
                <p className="text-sm text-muted-foreground max-w-sm">
                  {t('healthData.syncing.description')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Favorites first (graphs + sleep); each item appears only here when favorited */}
      {(() => {
        const resolvedFavoriteKeys = favoriteGraphs.filter(
          (k) =>
            k === SLEEP_FAVORITE_KEY ||
            (connected === true &&
              ALL_CHART_METRICS.some((c) => c.metric === k))
        );
        if (resolvedFavoriteKeys.length === 0) return null;
        return (
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
            <Star className="h-4 w-4 fill-primary text-primary" />
            {t('healthData.sections.favorites')}
          </h2>
          <div className="space-y-4">
            {resolvedFavoriteKeys.map((metricKey) => {
              if (metricKey === SLEEP_FAVORITE_KEY) {
                return (
                  <SleepTrackingCard
                    key={SLEEP_FAVORITE_KEY}
                    sleepLogs={sleepLogs}
                    healthSleepData={connected ? healthSleepData : []}
                    weeklyAverage={getSleepWeeklyAverage()}
                    trend={getSleepTrend()}
                    onLogSleep={logSleep}
                    variant="healthData"
                    isFavorite
                    onToggleFavorite={() => toggleFavorite(SLEEP_FAVORITE_KEY)}
                  />
                );
              }
              if (!connected) return null;
              const config = ALL_CHART_METRICS.find((c) => c.metric === metricKey);
              if (!config) return null;
              const Icon = config.icon;
              return (
                <HealthMetricChart
                  key={metricKey}
                  metric={config.metric}
                  label={config.label}
                  icon={Icon}
                  unit={config.unit}
                  formatter={
                    config.metric === 'distance_km'
                      ? (v) => kmToDisplayDistance(v, units)
                      : undefined
                  }
                  analyzeMetricKey={config.metric}
                  infoContent={config.info}
                  isFavorite
                  onToggleFavorite={() => toggleFavorite(metricKey)}
                />
              );
            })}
          </div>
        </div>
        );
      })()}

      {/* Sleep — only when not pinned to Favorites (avoids duplicate) */}
      {!favoriteGraphs.includes(SLEEP_FAVORITE_KEY) && (
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
            <Moon className="h-4 w-4" />
            {t('healthData.sections.sleep')}
          </h2>
          <SleepTrackingCard
            sleepLogs={sleepLogs}
            healthSleepData={connected ? healthSleepData : []}
            weeklyAverage={getSleepWeeklyAverage()}
            trend={getSleepTrend()}
            onLogSleep={logSleep}
            variant="healthData"
            isFavorite={false}
            onToggleFavorite={() => toggleFavorite(SLEEP_FAVORITE_KEY)}
          />
        </div>
      )}

      {/* Apple Health charts - only when connected; skip metrics already in Favorites */}
      {connected && (() => {
        const activityMetrics = ALL_CHART_METRICS.slice(0, 6).filter(
          (c) => !favoriteGraphs.includes(c.metric)
        );
        const heartMetrics = ALL_CHART_METRICS.slice(6, 10).filter(
          (c) => !favoriteGraphs.includes(c.metric)
        );
        return (
          <>
            {activityMetrics.length > 0 && (
              <>
                <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Activity className="h-4 w-4" />
                  {t('healthData.sections.activityFitness')}
                </h2>
                <div className="space-y-4">
                  {activityMetrics.map((config) => {
                    const Icon = config.icon;
                    return (
                      <HealthMetricChart
                        key={config.metric}
                        metric={config.metric}
                        label={config.label}
                        icon={Icon}
                        unit={config.unit}
                        formatter={
                          config.metric === 'distance_km'
                            ? (v) => kmToDisplayDistance(v, units)
                            : undefined
                        }
                        analyzeMetricKey={config.metric}
                        infoContent={config.info}
                        isFavorite={favoriteGraphs.includes(config.metric)}
                        onToggleFavorite={() => toggleFavorite(config.metric)}
                      />
                    );
                  })}
                </div>
              </>
            )}

            {heartMetrics.length > 0 && (
              <>
                <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mt-6 mb-3 flex items-center gap-2">
                  <Heart className="h-4 w-4" />
                  {t('healthData.sections.heartFitness')}
                </h2>
                <div className="space-y-4">
                  {heartMetrics.map((config) => {
                    const Icon = config.icon;
                    return (
                      <HealthMetricChart
                        key={config.metric}
                        metric={config.metric}
                        label={config.label}
                        icon={Icon}
                        unit={config.unit}
                        analyzeMetricKey={config.metric}
                        infoContent={config.info}
                        isFavorite={favoriteGraphs.includes(config.metric)}
                        onToggleFavorite={() => toggleFavorite(config.metric)}
                      />
                    );
                  })}
                </div>
              </>
            )}
          </>
        );
      })()}

      {/* Extra bottom spacer so sleep graph and all content can scroll fully into view above nav */}
      <div className="h-40 shrink-0" aria-hidden />
    </div>
  );
};
